import { useCallback, useEffect, useRef, useState } from "react";
import type { TableState } from "@/types/tableState.types";
import type { SortItem } from "@/components/UI/TableToolbar/Sorting/types";
import { getNextSortingForColumn } from "@/components/Templates/dashboardTableHeaderUtils";

const SORT_DEBOUNCE_MS = 200;

type UseDashboardTableSortingOptions = {
  filters: TableState["filters"];
  sorting: SortItem[];
  setSorting: (sorting: SortItem[]) => void;
};

type UseDashboardTableSortingResult = {
  pendingSorting: SortItem[] | null;
  toggleSorting: (field: string) => void;
};

export const useDashboardTableSorting = ({
  filters,
  sorting,
  setSorting,
}: UseDashboardTableSortingOptions): UseDashboardTableSortingResult => {
  const [pendingSorting, setPendingSorting] = useState<SortItem[] | null>(null);
  const pendingSortingRef = useRef<SortItem[] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingSorting = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingSortingRef.current = null;
  }, []);

  useEffect(() => {
    clearPendingSorting();
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setPendingSorting(null);
  }, [clearPendingSorting, filters, sorting]);

  useEffect(() => clearPendingSorting, [clearPendingSorting]);

  const toggleSorting = useCallback(
    (field: string) => {
      const nextSorting = getNextSortingForColumn(
        pendingSortingRef.current ?? sorting,
        field,
      );

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      pendingSortingRef.current = nextSorting;
      setPendingSorting(nextSorting);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (pendingSortingRef.current !== nextSorting) return;

        pendingSortingRef.current = null;
        setPendingSorting(null);
        setSorting(nextSorting);
      }, SORT_DEBOUNCE_MS);
    },
    [setSorting, sorting],
  );

  return { pendingSorting, toggleSorting };
};
